import { useCallback, useEffect, useState } from 'react'
import { addPasskey, credentials, removePasskey, type Credential, type User } from '../lib/auth'
import { enablePush, pushState, type PushState } from '../lib/push'
import { get, post } from '../lib/api'
import { isNativeIos, NATIVE_PUSH_CHANGED } from '../lib/nativePush'
import { t } from '../lib/i18n'

interface Inbox {
  id: string
  title: string
  body: string | null
  target: string | null
  created_at: number
  read_at: number | null
}

/**
 * The account screen: passkeys, notifications, inbox.
 *
 * Device management is not an advanced setting here — with no password, the list
 * of registered passkeys IS the account's security, and the recovery warning is
 * the most important thing on the page.
 */
export default function Account({ user }: { user: User }) {
  const [creds, setCreds] = useState<Credential[]>([])
  const [warning, setWarning] = useState<string | null>(null)
  const [push, setPush] = useState<PushState>('off')
  const [inbox, setInbox] = useState<Inbox[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [c, p, n] = await Promise.all([
      credentials(),
      pushState(user.id),
      get<{ notifications: Inbox[] }>('/api/notifications').catch(() => ({ notifications: [] })),
    ])
    setCreds(c.credentials)
    setWarning(c.warning)
    setPush(p)
    setInbox(n.notifications)
  }, [user.id])

  useEffect(() => {
    void load()
    const refreshPush = () => { void pushState(user.id).then(setPush) }
    window.addEventListener(NATIVE_PUSH_CHANGED, refreshPush)
    window.addEventListener('focus', refreshPush)
    document.addEventListener('visibilitychange', refreshPush)
    return () => {
      window.removeEventListener(NATIVE_PUSH_CHANGED, refreshPush)
      window.removeEventListener('focus', refreshPush)
      document.removeEventListener('visibilitychange', refreshPush)
    }
  }, [load, user.id])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await load()
    } catch (err) {
      const name = (err as { name?: string }).name
      setError(name === 'NotAllowedError' || name === 'AbortError' ? t('auth.cancelled') : (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <section className="card">
        <h2>{t('account.passkeys')}</h2>

        {warning && <p className="warn" role="alert">{warning}</p>}

        <ul className="list">
          {creds.map((c) => (
            <li key={c.id}>
              <div>
                <strong>{c.deviceName}</strong>
                <div className="muted small">
                  {c.syncedToProvider ? t('account.synced') : t('account.deviceOnly')}
                  {' · '}
                  {/* A passkey enrolled but never used is one that never worked. */}
                  {c.lastUsedAt ? `${t('account.lastUsed')} ${new Date(c.lastUsedAt).toLocaleDateString()}` : t('account.neverUsed')}
                </div>
              </div>
              <button
                disabled={busy || creds.length <= 1}
                title={creds.length <= 1 ? 'This is the only way into the account.' : undefined}
                onClick={() => run(() => removePasskey(c.id))}
              >
                {t('account.removePasskey')}
              </button>
            </li>
          ))}
        </ul>

        <button className="primary" disabled={busy} onClick={() => run(() => addPasskey())}>
          {t('account.addPasskey')}
        </button>
        {error && <p className="error" role="alert">{error}</p>}
      </section>

      <section className="card">
        <h2>{t('notify.inbox')}</h2>

        {push === 'blocked' && <p className="warn">{t(isNativeIos() ? 'notify.nativeBlocked' : 'notify.blocked')}</p>}
        {(push === 'off' || (push === 'blocked' && isNativeIos())) && (
          // Must be a real click: Safari refuses the permission prompt outside a
          // user gesture, and refuses it permanently for the origin.
          <button disabled={busy} onClick={() => run(() => enablePush(user.displayName, user.id))}>
            {t(push === 'blocked' ? 'notify.checkPermission' : 'notify.enable')}
          </button>
        )}
        {push === 'on' && <p className="muted small">{t('notify.enabled')}</p>}

        <ul className="list">
          {inbox.length === 0 && <li className="muted">{t('notify.empty')}</li>}
          {inbox.map((n) => (
            <li key={n.id} className={n.read_at ? 'read' : ''}>
              <div>
                <strong>{n.title}</strong>
                <div className="muted small">{n.body}</div>
              </div>
              {!n.read_at && (
                <button onClick={() => run(() => post(`/api/notifications/${n.id}/read`))}>{t('notify.markRead')}</button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </>
  )
}
